import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AccountSetting.css';
import { API_URL } from '../config/data';
import { logout, checkAuth, handleAuthError } from '../utils/auth';

const AccountSetting = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Состояния для данных пользователя
  const [user, setUser] = useState(null);
  const [passport, setPassport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Состояния для редактирования
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    phone: ''
  });
  
  const [passportEditData, setPassportEditData] = useState({
    series: '',
    number: '',
    issuedBy: '',
    issueDate: '',
    departmentCode: '',
    gender: '',
    birthDate: ''
  });
  
  // Состояния для смены пароля
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  
  // Состояние для метода авторизации
  const [authMethod, setAuthMethod] = useState('password');
  const [isChangingAuthMethod, setIsChangingAuthMethod] = useState(false);
  
  // Состояния для ошибок валидации
  const [personalErrors, setPersonalErrors] = useState({});
  const [passportErrors, setPassportErrors] = useState({});

  const tabs = [
    { id: 0, name: 'Персональные данные' },
    { id: 1, name: 'Настройки' }
  ];

  // Функция для получения токена из localStorage
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Функция для преобразования даты в формат для HTML input date (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    // Если дата уже в формате YYYY-MM-DD, возвращаем как есть
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Если дата в формате DD.MM.YYYY, преобразуем в YYYY-MM-DD
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('.');
      return `${year}-${month}-${day}`;
    }
    
    // Пытаемся распарсить дату и преобразовать в YYYY-MM-DD
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        // Используем локальную дату для избежания проблем с часовыми поясами
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch (error) {
      console.error('Ошибка при форматировании даты:', error);
    }
    
    return '';
  };

  // Функция для загрузки профиля пользователя
  const fetchUserProfile = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Ошибка при получении профиля:', error);
      throw error;
    }
  };

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    // Проверяем авторизацию перед загрузкой данных
    if (!checkAuth()) {
      return;
    }

    const loadProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const profileData = await fetchUserProfile();
        setUser(profileData.user);
        setPassport(profileData.passport);
        
        // Устанавливаем метод авторизации из профиля
        setAuthMethod(profileData.user.authMethod || 'password');

        // Инициализируем данные для редактирования
        setEditData({
          firstName: profileData.user.firstName || '',
          lastName: profileData.user.lastName || '',
          middleName: profileData.user.middleName || '',
          phone: profileData.user.phone || ''
        });

        if (profileData.passport) {
          console.log('Исходные даты из API:', {
            issue_date: profileData.passport.issue_date,
            birth_date: profileData.passport.birth_date
          });
          
          const passportData = {
            series: profileData.passport.series || '',
            number: profileData.passport.number || '',
            issuedBy: profileData.passport.issued_by || '',
            issueDate: formatDateForInput(profileData.passport.issue_date) || '',
            departmentCode: profileData.passport.department_code || '',
            gender: profileData.passport.gender || '',
            birthDate: formatDateForInput(profileData.passport.birth_date) || ''
          };
          
          console.log('Конвертированные даты:', {
            issueDate: passportData.issueDate,
            birthDate: passportData.birthDate
          });
          
          setPassportEditData(passportData);
        }
      } catch (error) {
        console.error('Ошибка при загрузке профиля:', error);
        
        // Обрабатываем ошибки авторизации
        if (handleAuthError(error)) {
          return;
        }
        
        setError('Ошибка при загрузке данных профиля');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  // Проверяем якорь в URL при загрузке компонента
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#change') {
      setShowPasswordPopup(true);
      // Убираем якорь из URL
      window.history.replaceState(null, null, window.location.pathname);
    }
  }, []);

  // Обработчик для закрытия модального окна по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showPasswordPopup) {
        setShowPasswordPopup(false);
      }
    };

    if (showPasswordPopup) {
      document.addEventListener('keydown', handleEscape);
      document.body.classList.add('popup-open');
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.classList.remove('popup-open');
      };
    }
  }, [showPasswordPopup]);

  // Функция для обновления персональных данных
  const updatePersonalData = async () => {
    try {
      setIsSaving(true);
      setPersonalErrors({});
      const token = getAuthToken();
      
      const response = await axios.put(`${API_URL}/api/profile`, editData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        // Обновляем данные пользователя
        setUser(prev => ({
          ...prev,
          ...editData
        }));
        setIsEditMode(false);
        console.log('Персональные данные обновлены');
      }
    } catch (error) {
      console.error('Ошибка при обновлении персональных данных:', error);
      
      // Обрабатываем ошибки авторизации
      if (handleAuthError(error)) {
        return;
      }
      
      if (error.response?.data?.errors) {
        // Обрабатываем ошибки валидации
        const validationErrors = {};
        error.response.data.errors.forEach(err => {
          validationErrors[err.path] = err.msg;
        });
        setPersonalErrors(validationErrors);
      } else {
        setError('Ошибка при сохранении данных');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Функция для обновления паспортных данных
  const updatePassportData = async () => {
    try {
      setIsSaving(true);
      setPassportErrors({});
      const token = getAuthToken();
      
      // Преобразуем данные в формат snake_case для API
      const apiData = {
        series: passportEditData.series,
        number: passportEditData.number,
        issued_by: passportEditData.issuedBy,
        issue_date: passportEditData.issueDate, // уже в формате YYYY-MM-DD
        department_code: passportEditData.departmentCode,
        gender: passportEditData.gender,
        birth_date: passportEditData.birthDate // уже в формате YYYY-MM-DD
      };
      
      console.log('Отправляем на сервер:', apiData);
      console.log('issue_date:', apiData.issue_date, 'тип:', typeof apiData.issue_date);
      console.log('birth_date:', apiData.birth_date, 'тип:', typeof apiData.birth_date);
      
      const response = await axios.put(`${API_URL}/api/profile/passport`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        setPassport(prev => ({
          ...prev,
          ...passportEditData
        }));
        console.log('Паспортные данные обновлены');
      }
    } catch (error) {
      console.error('Ошибка при обновлении паспортных данных:', error);
      
      // Обрабатываем ошибки авторизации
      if (handleAuthError(error)) {
        return;
      }
      
      if (error.response?.data?.errors) {
        // Обрабатываем ошибки валидации
        const validationErrors = {};
        error.response.data.errors.forEach(err => {
          // Преобразуем snake_case обратно в camelCase для отображения ошибок
          const fieldName = err.path === 'issued_by' ? 'issuedBy' :
                           err.path === 'issue_date' ? 'issueDate' :
                           err.path === 'department_code' ? 'departmentCode' :
                           err.path === 'birth_date' ? 'birthDate' : err.path;
          validationErrors[fieldName] = err.msg;
        });
        setPassportErrors(validationErrors);
      } else {
        setError('Ошибка при сохранении паспортных данных');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Функция для смены пароля
  const changePassword = async () => {
    try {
      setIsSaving(true);
      setPasswordErrors({});
      
      const token = getAuthToken();
      
      const response = await axios.post(`${API_URL}/api/auth/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        setShowPasswordPopup(false);
        setPasswordData({ currentPassword: '', newPassword: '' });
        console.log('Пароль успешно изменен');
      }
    } catch (error) {
      console.error('Ошибка при смене пароля:', error);
      
      // Обрабатываем ошибки авторизации
      if (handleAuthError(error)) {
        return;
      }
      
      if (error.response?.data?.errors) {
        setPasswordErrors(error.response.data.errors);
      } else {
        setPasswordErrors({ general: 'Ошибка при смене пароля' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    changePassword();
  };

  const handlePasswordButtonClick = () => {
    setShowPasswordPopup(true);
  };

  const handlePopupClose = (e) => {
    if (e.target === e.currentTarget) {
      setShowPasswordPopup(false);
    }
  };

  // Обработчики для изменения данных
  const handleEditDataChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Очищаем ошибку при изменении поля
    if (personalErrors[field]) {
      setPersonalErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePassportEditDataChange = (field, value) => {
    setPassportEditData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Очищаем ошибку при изменении поля
    if (passportErrors[field]) {
      setPassportErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePasswordDataChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Очищаем ошибку при изменении поля
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Функция для смены метода авторизации
  const changeAuthMethod = async (newMethod) => {
    try {
      setIsChangingAuthMethod(true);
      const token = getAuthToken();
      
      if (!token) {
        throw new Error('Токен авторизации не найден');
      }

      const response = await axios.put(`${API_URL}/api/profile/auth-method`, {
        authMethod: newMethod
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.message === 'Authentication method changed successfully') {
        setAuthMethod(newMethod);
        // Обновляем данные пользователя
        setUser(prev => ({
          ...prev,
          authMethod: newMethod
        }));
        console.log('Метод авторизации успешно изменен на:', newMethod);
      }
    } catch (error) {
      console.error('Ошибка при смене метода авторизации:', error);
      
      // Обрабатываем ошибки авторизации
      if (handleAuthError(error)) {
        return;
      }
      
      alert('Ошибка при смене метода авторизации. Попробуйте еще раз.');
    } finally {
      setIsChangingAuthMethod(false);
    }
  };

  // Функция для получения полного имени
  const getFullName = () => {
    if (!user) return 'Загрузка...';
    return `${user.lastName} ${user.firstName} ${user.middleName}`.trim();
  };

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  };

  // Функция для форматирования пола
  const formatGender = (gender) => {
    if (!gender) return 'Не указано';
    return gender === 'male' ? 'Мужской' : 'Женский';
  };

  // Функция для форматирования номера паспорта (XXXX XXXXXX)
  const formatPassportNumber = (series, number) => {
    if (!series && !number) return '';
    const seriesStr = series || '';
    const numberStr = number || '';
    
    if (seriesStr.length === 4 && numberStr.length > 0) {
      return `${seriesStr} ${numberStr}`;
    } else if (seriesStr.length > 0) {
      return seriesStr;
    }
    return '';
  };

  // Функция для форматирования кода подразделения (XXX-XXX)
  const formatDepartmentCode = (code) => {
    if (!code) return '';
    const digits = code.replace(/\D/g, '');
    
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      return `${digits.slice(0, 3)}-${digits.slice(3, 6)}`;
    }
  };

  if (isLoading) {
    return (
      <div className="wrapper">
        <div className="loading">
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wrapper">
        <div className="error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      <header data-fls-header-accaunt="" className="header-accaunt">
        <div className="header-accaunt__container">
          <div className="header-accaunt__body">
            <a href="/account" className="header-accaunt__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
            <div className="header-accaunt__actions">
              <div className="header-accaunt__settings">
                <a href="/account/setting" className="--icon-settings"></a>
              </div>
              <div className="header-accaunt__profile">
                <a href="" className="--icon-profile">
                  <div className="header-accaunt__content-profile">
                    <span>{user ? `${user.lastName} ${user.firstName}` : 'Загрузка...'}</span>
                    <span>{user?.role || 'Квалифицированный управляющий'}</span>
                  </div>
                </a>
              </div>
              <button 
                data-fls-button="" 
                type="button" 
                className="header-accaunt__logout header-accaunt__logout--icon-log-out"
                onClick={logout}
              ></button>
            </div>
          </div>
        </div>
      </header>
      <main className="page account">
        <div className="account__container">
          <div className="account__body">
            <nav data-fls-block="" data-fls-aside="" className="aside">
              <ul className="aside__list">
                <li className="aside__item">
                  <a href="/account" className="aside__link aside__link--active aside__link--icon-home">Главная</a>
                </li>
                <li className="aside__item">
                  <a href="/account/expense" className="aside__link aside__link--icon-wallet">Счета</a>
                </li>
                <li className="aside__item">
                  <a href="/account/history" className="aside__link aside__link--icon-history">История</a>
                </li>
                <li className="aside__item">
                  <a href="#" className="aside__link aside__link--icon-withdraw">Вывести</a>
                </li>
              </ul>
              <div className="aside__actions">
                <a href="/account/withdraw" className="aside__action aside__action--icon-withdraw">Вывести деньги</a>
                <a href="/account/check" className="aside__action aside__action--icon-deposit">Пополнить баланс</a>
              </div>
            </nav>
            <div className="account__content">
              <h3 className="account__title account__title--title-25">{getFullName()}</h3>
              <div data-fls-tabs="" data-fls-tabs-animate="" className="account__tabs tabs">
                <nav data-fls-tabs-titles="" className="tabs__navigation">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`tabs__title ${activeTab === tab.id ? '--tab-active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
                <div data-fls-tabs-body="" data-fls-maxwidth="" className="tabs__content">
                  {activeTab === 0 && (
                    <div data-fls-block="" className="tabs__body setting-privacy">
                      <div className="setting-privacy__block">
                        <div className="setting-privacy__head">
                          <h4 className="setting-privacy__title setting-privacy__title--title-20">Персональные данные</h4>
                          <button 
                            type="button" 
                            data-fls-button="" 
                            className="setting-privacy__edit edit edit--icon-edit"
                            onClick={isEditMode ? updatePersonalData : () => setIsEditMode(true)}
                            disabled={isSaving}
                          >
                            <span>{isSaving ? 'Сохранение...' : (isEditMode ? 'Сохранить' : 'Редактировать')}</span>
                          </button>
                        </div>
                        <div className="setting-privacy__items">
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Имя</div>
                            <div className="setting-privacy__info">
                              {isEditMode ? (
                                <input 
                                  type="text" 
                                  value={editData.firstName}
                                  onChange={(e) => handleEditDataChange('firstName', e.target.value)}
                                  className={`setting-privacy__input ${personalErrors.firstName ? '--form-error' : ''}`}
                                />
                              ) : (
                                <span>{user?.firstName || 'Не указано'}</span>
                              )}
                              {personalErrors.firstName && (
                                <div className="setting-privacy__error">{personalErrors.firstName}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Фамилия</div>
                            <div className="setting-privacy__info">
                              {isEditMode ? (
                                <input 
                                  type="text" 
                                  value={editData.lastName}
                                  onChange={(e) => handleEditDataChange('lastName', e.target.value)}
                                  className={`setting-privacy__input ${personalErrors.lastName ? '--form-error' : ''}`}
                                />
                              ) : (
                                <span>{user?.lastName || 'Не указано'}</span>
                              )}
                              {personalErrors.lastName && (
                                <div className="setting-privacy__error">{personalErrors.lastName}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Отчество</div>
                            <div className="setting-privacy__info">
                              {isEditMode ? (
                                <input 
                                  type="text" 
                                  value={editData.middleName}
                                  onChange={(e) => handleEditDataChange('middleName', e.target.value)}
                                  className={`setting-privacy__input ${personalErrors.middleName ? '--form-error' : ''}`}
                                />
                              ) : (
                                <span>{user?.middleName || 'Не указано'}</span>
                              )}
                              {personalErrors.middleName && (
                                <div className="setting-privacy__error">{personalErrors.middleName}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Мобильный телефон</div>
                            <div className="setting-privacy__info">
                              {isEditMode ? (
                                <input 
                                  type="tel" 
                                  value={editData.phone}
                                  onChange={(e) => handleEditDataChange('phone', e.target.value)}
                                  className={`setting-privacy__input ${personalErrors.phone ? '--form-error' : ''}`}
                                />
                              ) : (
                                <span>{user?.phone || 'Не указано'}</span>
                              )}
                              {personalErrors.phone && (
                                <div className="setting-privacy__error">{personalErrors.phone}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">E-mail</div>
                            <div className="setting-privacy__info">
                              <span>{user?.email || 'Не указано'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="setting-privacy__block">
                        <div className="setting-privacy__head">
                          <h3 className="setting-privacy__title setting-privacy__title--title-20">Паспортные данные</h3>
                          <button 
                            type="button" 
                            data-fls-button="" 
                            className="setting-privacy__edit edit edit--icon-edit"
                            onClick={updatePassportData}
                            disabled={isSaving}
                          >
                            <span>{isSaving ? 'Сохранение...' : 'Сохранить'}</span>
                          </button>
                        </div>
                        <div className="setting-privacy__items">
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Серия и номер паспорта</div>
                            <div className="setting-privacy__info">
                              <input 
                                type="text" 
                                value={formatPassportNumber(passportEditData.series, passportEditData.number)}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  const series = value.slice(0, 4);
                                  const number = value.slice(4, 10);
                                  handlePassportEditDataChange('series', series);
                                  handlePassportEditDataChange('number', number);
                                }}
                                className={`setting-privacy__input ${passportErrors.series || passportErrors.number ? '--form-error' : ''}`}
                                placeholder="1234 567890"
                                maxLength="11"
                              />
                              {(passportErrors.series || passportErrors.number) && (
                                <div className="setting-privacy__error">{passportErrors.series || passportErrors.number}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Кем выдан</div>
                            <div className="setting-privacy__info">
                              <input 
                                type="text" 
                                value={passportEditData.issuedBy}
                                onChange={(e) => handlePassportEditDataChange('issuedBy', e.target.value)}
                                className={`setting-privacy__input ${passportErrors.issuedBy ? '--form-error' : ''}`}
                                placeholder="ОУФМС России по г. Москве"
                              />
                              {passportErrors.issuedBy && (
                                <div className="setting-privacy__error">{passportErrors.issuedBy}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Дата выдачи</div>
                            <div className="setting-privacy__info">
                              <input 
                                type="date" 
                                value={formatDateForInput(passportEditData.issueDate)}
                                onChange={(e) => handlePassportEditDataChange('issueDate', e.target.value)}
                                className={`setting-privacy__input ${passportErrors.issueDate ? '--form-error' : ''}`}
                              />
                              {passportErrors.issueDate && (
                                <div className="setting-privacy__error">{passportErrors.issueDate}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Код подразделения</div>
                            <div className="setting-privacy__info">
                              <input 
                                type="text" 
                                value={formatDepartmentCode(passportEditData.departmentCode)}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  const formatted = value.slice(0, 6);
                                  handlePassportEditDataChange('departmentCode', formatted);
                                }}
                                className={`setting-privacy__input ${passportErrors.departmentCode ? '--form-error' : ''}`}
                                placeholder="123-456"
                                maxLength="7"
                              />
                              {passportErrors.departmentCode && (
                                <div className="setting-privacy__error">{passportErrors.departmentCode}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Пол</div>
                            <div className="setting-privacy__info">
                              <select 
                                value={passportEditData.gender}
                                onChange={(e) => handlePassportEditDataChange('gender', e.target.value)}
                                className={`setting-privacy__input ${passportErrors.gender ? '--form-error' : ''}`}
                              >
                                <option value="">Выберите пол</option>
                                <option value="male">Мужской</option>
                                <option value="female">Женский</option>
                              </select>
                              {passportErrors.gender && (
                                <div className="setting-privacy__error">{passportErrors.gender}</div>
                              )}
                            </div>
                          </div>
                          <div className="setting-privacy__item">
                            <div className="setting-privacy__label">Дата рождения</div>
                            <div className="setting-privacy__info">
                              <input 
                                type="date" 
                                value={formatDateForInput(passportEditData.birthDate)}
                                onChange={(e) => handlePassportEditDataChange('birthDate', e.target.value)}
                                className={`setting-privacy__input ${passportErrors.birthDate ? '--form-error' : ''}`}
                              />
                              {passportErrors.birthDate && (
                                <div className="setting-privacy__error">{passportErrors.birthDate}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeTab === 1 && (
                    <div data-fls-block="" className="tabs__body setting-password">
                      <h4 className="setting-password__title setting-password__title--title-20">Настройки входа</h4>
                      
                      {/* Переключатель метода авторизации */}
                      <div className="setting-password__auth-method">
                        <div className="setting-password__label setting-password__label--title-18">Метод входа</div>
                        <div className="setting-password__toggle">
                          <button 
                            type="button"
                            className={`setting-password__toggle-btn ${authMethod === 'password' ? 'active' : ''}`}
                            onClick={() => changeAuthMethod('password')}
                            disabled={isChangingAuthMethod}
                          >
                            {isChangingAuthMethod && authMethod === 'password' ? 'Загрузка...' : 'По паролю'}
                          </button>
                          <button 
                            type="button"
                            className={`setting-password__toggle-btn ${authMethod === 'sms' ? 'active' : ''}`}
                            onClick={() => changeAuthMethod('sms')}
                            disabled={isChangingAuthMethod}
                          >
                            {isChangingAuthMethod && authMethod === 'sms' ? 'Загрузка...' : 'По SMS'}
                          </button>
                        </div>
                      </div>

                      {/* Секция для входа по паролю */}
                      {authMethod === 'password' && (
                        <>
                          <div className="setting-password__label setting-password__label--title-18">Вход по паролю</div>
                          <button 
                            type="button" 
                            data-fls-popup-link="change" 
                            data-fls-button="" 
                            className="setting-password__button button"
                            onClick={handlePasswordButtonClick}
                          >
                            Изменить пароль
                          </button>
                        </>
                      )}

                      {/* Секция для входа по SMS */}
                      {authMethod === 'sms' && (
                        <div className="setting-password__sms-info">
                          <div className="setting-password__label setting-password__label--title-18">Вход по SMS</div>
                          <p className="setting-password__description">
                            Для входа в систему будет использоваться SMS-код, отправляемый на номер телефона: 
                            <strong> {user?.phone || 'Не указан'}</strong>
                          </p>
                          <p className="setting-password__note">
                            Для изменения номера телефона перейдите в раздел "Персональные данные"
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Popup для смены пароля */}
      {showPasswordPopup && (
        <div data-fls-popup="change" aria-hidden="false" className={`popup ${showPasswordPopup ? 'show' : ''}`} onClick={handlePopupClose}>
          <div data-fls-popup-wrapper="" className="popup__wrapper">
            <div data-fls-popup-body="" className="popup__body change">
              <h3 className="change__title change__title--title-20">Сменить пароль</h3>
              <form data-fls-form="" action="#" className="change__form" onSubmit={handlePasswordSubmit}>
                <div className="change__password password">
                  <input 
                    data-fls-input="" 
                    required 
                    autoComplete="off" 
                    type={showCurrentPassword ? "text" : "password"} 
                    name="currentPassword" 
                    value={passwordData.currentPassword}
                    onChange={(e) => handlePasswordDataChange('currentPassword', e.target.value)}
                    data-error="Помилка" 
                    placeholder="Текущий пароль" 
                    className="input input--password"
                  />
                  <button 
                    type="button" 
                    className="password__eye password__eye--icon-eye"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  ></button>
                </div>
                {passwordErrors.currentPassword && (
                  <div className="change__error">{passwordErrors.currentPassword}</div>
                )}
                <div className="change__password password">
                  <input 
                    data-fls-input="" 
                    required 
                    autoComplete="off" 
                    type={showNewPassword ? "text" : "password"} 
                    name="newPassword" 
                    value={passwordData.newPassword}
                    onChange={(e) => handlePasswordDataChange('newPassword', e.target.value)}
                    data-error="Помилка" 
                    placeholder="Новый пароль" 
                    className="input input--password"
                  />
                  <button 
                    type="button" 
                    className="password__eye password__eye--icon-eye"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  ></button>
                </div>
                {passwordErrors.newPassword && (
                  <div className="change__error">{passwordErrors.newPassword}</div>
                )}
                {passwordErrors.general && (
                  <div className="change__error">{passwordErrors.general}</div>
                )}
                <div className="change__text">
                  <p>
                    Длина пароля — от 8 до 32 символов. Пароль должен содержать как минимум 2 заглавные и 3 строчные буквы, а также 2 цифры
                  </p>
                </div>
                <div className="change__actions">
                  <button data-fls-button="" type="submit" className="form-change__button button">Сохранить</button>
                  <button 
                    data-fls-popup-close="" 
                    data-fls-button="" 
                    type="button" 
                    className="form-change__button button button--gray"
                    onClick={() => setShowPasswordPopup(false)}
                  >
                    Отменить
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSetting;